import { useQuery } from "@apollo/client/react";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import {
  PublishedQuestionnaireGroupPartsDocument,
  QuestionnaireGroupEvaluationDocument,
} from "@/graphql/generated/operations";
import { isPowerQuizGroup } from "@/lib/questionnaire-group";

export interface PowerQuizState {
  questionnaireGroupId: string;
  complete: boolean;
  included: boolean;
  started: boolean;
}

export function usePowerQuizStatus(element: string | undefined): PowerQuizState | null {
  const canQuiz = useHasFeature(FEATURE_CATALOG.growPlay.key);
  const groups = useQuery(PublishedQuestionnaireGroupPartsDocument, {
    skip: !canQuiz || !element,
    fetchPolicy: "cache-and-network",
  });
  const quiz = groups.data?.publishedQuestionnaireGroups.find(isPowerQuizGroup);
  const available = !!quiz && quiz.itemCount > 0 && quiz.parts.length > 0;
  const evaluation = useQuery(QuestionnaireGroupEvaluationDocument, {
    variables: { questionnaireGroupId: quiz?.questionnaireGroupId ?? "" },
    skip: !canQuiz || !element || !available,
    fetchPolicy: "cache-and-network",
  });
  if (!canQuiz || !element || !available || groups.error || evaluation.error || !evaluation.data)
    return null;

  const rows = evaluation.data.questionnaireGroupEvaluation;
  const row = rows.find((entry) => String(entry.element) === element);
  if (!row) return null;
  return {
    questionnaireGroupId: quiz.questionnaireGroupId,
    complete: quiz.parts.every((part) => part.answered),
    // A replaced answer choice can reset progress while the stored answer still contributes.
    included: typeof row.quizResult === "number",
    started: quiz.parts.some((part) => part.answered) || rows.some(
      (entry) => typeof entry.quizResult === "number" || entry.answeredItemCount > 0,
    ),
  };
}
